/**
 * @module UseContainerTriageInventory
 * @purpose Loads a receipt's grouped inventory (server-filtered by the
 *   active kind filter, per the Database-First constraint -- no client-side
 *   re-filtering) and owns the "run inventory" sub-flow: trigger ->
 *   operationId handed to the shared OperationProgress component -> on
 *   completion, refetch. If inventory is already `in_progress` (triggered
 *   elsewhere, no operationId available here) polls the GET itself via
 *   usePollingRefetch until it leaves that status.
 * @depends services/evidence-containers, use-polling-refetch
 * @dependedOnBy use-container-triage
 *
 * @rules
 * - State: local useState; fetches on mount and whenever `kind` changes,
 *   refetches after trigger/complete
 * - Performance: stale-request requestIdRef guard so a fast kind-filter
 *   change cannot let an in-flight older response overwrite a newer one
 * - Errors: sets `error` on either the initial load or the trigger call;
 *   never throws from hook body
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getContainerInventory,
  triggerContainerInventory,
} from '../../../services/evidence-containers'
import type { GroupedInventoryResponse, InventoryItemKind } from '../../../types/evidence-containers'
import { usePollingRefetch } from '../../../hooks/use-polling-refetch'

export interface UseContainerTriageInventoryResult {
  inventory: GroupedInventoryResponse | null
  loading: boolean
  error: string | null
  inventoryOperationId: string | null
  triggerInventory: () => Promise<void>
  handleInventoryOperationComplete: () => void
  refetch: () => Promise<void>
}

export function useContainerTriageInventory(
  caseId: string,
  receiptId: string,
  kind: InventoryItemKind | null
): UseContainerTriageInventoryResult {
  const [inventory, setInventory] = useState<GroupedInventoryResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [inventoryOperationId, setInventoryOperationId] = useState<string | null>(null)

  const requestIdRef = useRef(0)

  const refetch = useCallback(async () => {
    const thisId = ++requestIdRef.current
    try {
      const result = await getContainerInventory(caseId, receiptId, {
        groupBy: 'artefactType',
        ...(kind ? { kind } : {}),
      })
      if (thisId !== requestIdRef.current) return
      setInventory(result)
      setError(null)
    } catch (err) {
      if (thisId !== requestIdRef.current) return
      setError(err instanceof Error ? err.message : 'Failed to load container inventory')
    } finally {
      if (thisId === requestIdRef.current) setLoading(false)
    }
  }, [caseId, receiptId, kind])

  useEffect(() => {
    void refetch()
  }, [refetch])

  const triggerInventory = useCallback(async () => {
    try {
      const result = await triggerContainerInventory(caseId, receiptId)
      setInventoryOperationId(result.operationId)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start inventory')
    }
  }, [caseId, receiptId])

  const handleInventoryOperationComplete = useCallback(() => {
    setInventoryOperationId(null)
    void refetch()
  }, [refetch])

  const staleInProgress = inventoryOperationId === null && inventory?.inventoryStatus === 'in_progress'
  usePollingRefetch(refetch, staleInProgress)

  return {
    inventory,
    loading,
    error,
    inventoryOperationId,
    triggerInventory,
    handleInventoryOperationComplete,
    refetch,
  }
}
